import { useState, type FunctionComponent } from "react";
import { LinkOutlined } from "@ant-design/icons";
import { Button, Card, Descriptions, Grid, message, Space, Switch, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import truncate from "lodash.truncate";
import { useTranslation } from "react-i18next";

import { productActions, RequestStatus } from "@fitness-app/app-store";
import {
  PaymentTypeOption,
  ProductChargeType,
  ProductDurationType,
  ProductStatus,
  ProductType,
  type CrossSell,
  type EndAccessOffers,
  type InvoicesClient,
  type UpdateProductDTO,
  type UpsellTo,
} from "@fitness-app/data-models/entities/Product";
import { getProductLink } from "@fitness-app/utils/src/products/getProductLink";

import ModalForm from "~/components/ModalForm/ModalForm";
import { LIST_ITEM_SEPARATOR } from "~/constants/separators";
import ProductPage from "~/modules/Products/Product/ProductDetails/components/ProductPage";
import { usePaymentGatewayOptions } from "~/modules/Products/ProductForm/hooks/usePaymentGatewayOptions";
import ProductForm from "~/modules/Products/ProductForm/ProductForm";
import { getEventDetails, toEventDetailsFormValue } from "~/modules/Products/ProductForm/eventDetails";
import { type ProductFormModel } from "~/modules/Products/ProductForm/types";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { ProductPaymentGatewaysStatus } from "./components/ProductPaymentGatewasStatus";

const ProductDetails: FunctionComponent = () => {
  const { t } = useTranslation(["products", "common"]);
  const { details, productDetailsStatus } = useAppSelector((store) => store.product);
  const products = useAppSelector((store) => store.products.productsListData);
  const integration = useAppSelector((store) => store.productsSettings.data);

  const [productModel, setProductModel] = useState<ProductFormModel | null>(null);
  const [showProductForm, toggleProductForm] = useState<boolean>(false);
  const productAutomation = useAppSelector((store) => store.productAutomation.data);
  const { md } = Grid.useBreakpoint();
  const dispatch = useAppDispatch();
  const { tpayOptionsByKey, stripeOptionsByKey } = usePaymentGatewayOptions();

  const handleEditingProductModel = () => {
    if (!details) {
      return;
    }

    const model: ProductFormModel = {
      images: details.images || null,
      customStripeAccount: details.customStripeAccount,
      tpayIntegration: details.tpayIntegration,
      name: details.name,
      author: details.customAuthor || null,
      description: details.description || "",
      productChargeType: details.chargeType,
      isFree: details.chargeType === ProductChargeType.FREE,
      customInvoicesClient: details.customInvoicesClient
        ? `${details.customInvoicesClient.accountName}${LIST_ITEM_SEPARATOR}${details.customInvoicesClient.client}${LIST_ITEM_SEPARATOR}${details.customInvoicesClient.accountUrl}`
        : null,
      productType: details.type,
      upsellTo: Array.isArray(details.upsellTo) ? details.upsellTo.map((product) => product.productId) : [],
      crossSell: details.crossSell ? details.crossSell.products.map((product) => product.productId) : [],
      connectedAddons: details.connectedAddons
        ? details.connectedAddons.products.map((product) => product.productId)
        : [],
      connectedAddonsPrices: Object.fromEntries(
        (details.connectedAddons?.products ?? [])
          .filter((product) => product.priceId)
          .map((product) => [product.productId, product.priceId]),
      ),
      endAccessOffers: Array.isArray(details.endAccessOffers)
        ? details.endAccessOffers.map((product) => product.productId)
        : [],
      productDurationType: details.productDuration?.type || null,
      productDuration:
        details.productDuration?.type === ProductDurationType.UntilDate
          ? {
              untilDate: dayjs(details.productDuration.date),
            }
          : details.productDuration?.type === ProductDurationType.LastsFor
            ? {
                unit: details.productDuration.unit,
                count: details.productDuration.count,
              }
            : undefined,
      eventDetails: toEventDetailsFormValue(details.eventDetails),
      prices:
        details.chargeType === ProductChargeType.FREE
          ? []
          : details.prices.map((price) => ({
              id: price.id,
              amount: price.unit_amount ? price.unit_amount / 100 : 0,
              currency: price.currency,
              paymentType: price.type,
              paymentClient: price.paymentClient,
              billingPeriod:
                price.type === PaymentTypeOption.Recurring && price.stripePrice.recurring
                  ? [
                      price.stripePrice.recurring?.interval_count || "1",
                      price.stripePrice.recurring?.interval || "month",
                    ].join("-")
                  : null,
            })),
    };

    if (details.productDuration?.type === ProductDurationType.UntilDate) {
      model.productDuration;
    }

    setProductModel(model);
    toggleProductForm(true);
  };

  const handleSubmit = async (model: ProductFormModel) => {
    if (!details?.id) {
      void message.warning("Problem with load form model");
      return;
    }

    const { customInvoicesClient } = model;

    const [invoiceClientAccountName, invoiceClient, invoiceClientUrl] =
      customInvoicesClient?.split(LIST_ITEM_SEPARATOR) ?? [];

    const formattedData: UpdateProductDTO = {
      id: details.id,
      status: details.status,
      crossSell: {
        products:
          model.crossSell
            ?.map((id) => {
              const found = products.find((product) => product.id === id);
              if (found) {
                return {
                  productName: found.name,
                  productId: found.id,
                };
              } else {
                return null;
              }
            })
            .filter((item): item is CrossSell => !!item) ?? [],
      },
      connectedAddons: {
        products:
          model.connectedAddons
            ?.map((id): CrossSell | null => {
              const found = products.find((product) => product.id === id);
              if (found) {
                return {
                  productName: found.name,
                  productId: found.id,
                  // Pinned price for the add-on offer (TRE-1517); null = all visible prices.
                  priceId: model.connectedAddonsPrices?.[id] ?? null,
                };
              } else {
                return null;
              }
            })
            .filter((item): item is CrossSell => !!item) ?? [],
      },
      upsellTo: model.upsellTo
        ?.map((id) => {
          const found = products.find((product) => product.id === id);
          if (found) {
            return {
              productName: found.name,
              productId: found.id,
              checkoutId: integration?.stripeConnectedAccountId || null,
            };
          } else {
            return null;
          }
        })
        .filter((item): item is UpsellTo => !!item),
      endAccessOffers: model.endAccessOffers
        ?.map((id) => {
          const found = products.find((product) => product.id === id);
          if (found) {
            return {
              productName: found.name,
              productId: found.id,
            };
          } else {
            return null;
          }
        })
        .filter((item): item is EndAccessOffers => !!item),
      customInvoicesClient:
        customInvoicesClient && productModel?.productChargeType !== ProductChargeType.FREE
          ? {
              accountUrl: invoiceClientUrl || "",
              accountName: invoiceClientAccountName || "",
              client: invoiceClient as InvoicesClient,
            }
          : null,
      ...(details.type === ProductType.EVENT
        ? { eventDetails: getEventDetails(details.type, model.eventDetails) }
        : {}),
      description: model.description,
      name: model.name,
      images: model.images?.map((image) => (typeof image === "string" ? image : image.url)) ?? [],
    };
    const onSuccess = () => {
      toggleProductForm(false);
      setProductModel(null);
    };

    await dispatch(productActions.updateProduct({ payload: formattedData, onSuccess }));
  };

  return (
    <>
      <Card
        loading={productDetailsStatus === RequestStatus.SUBSCRIBING && !details}
        extra={
          <Button type="primary" onClick={handleEditingProductModel}>
            {t("button.editProduct")}
          </Button>
        }
      >
        <Space direction="vertical" size={24}>
          {details && (
            <Descriptions bordered labelStyle={{ width: md ? 250 : 320 }} layout={md ? "horizontal" : "vertical"}>
              {details.images.length ? (
                <Descriptions.Item label={t("productDetails.photo")} span={3}>
                  <div
                    style={{
                      width: "auto",
                      maxWidth: "150px",
                      maxHeight: "250px",
                      padding: 10,
                    }}
                  >
                    <img alt="product" src={details.images[0]} style={{ maxWidth: "100%", height: "auto" }} />
                  </div>
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label={t("productDetails.id")} span={3}>
                <Typography.Paragraph
                  copyable={{
                    tooltips: [details.id, t("common:copied")],
                    text: details.id,
                  }}
                  style={{ marginBottom: 0 }}
                  strong
                >
                  {truncate(details.id, { length: 20 })}
                </Typography.Paragraph>
              </Descriptions.Item>
              {details && (
                <Descriptions.Item label={t("productDetails.productPage")} span={3}>
                  <ProductPage product={details} />
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t("productDetails.name")} span={3}>
                {details.name}
              </Descriptions.Item>

              <Descriptions.Item label={t("productDetails.description")} span={3}>
                {details.description}
              </Descriptions.Item>

              <Descriptions.Item label={t("productDetails.type")} span={3}>
                {t(`productType.${details.type.toLowerCase()}`)}
              </Descriptions.Item>

              {details.eventDetails && (
                <Descriptions.Item label={t("productDetails.eventDetails")} span={3}>
                  <Typography.Text strong>
                    {dayjs(details.eventDetails.startsAt).format("DD.MM.YYYY HH:mm")}
                    {details.eventDetails.endsAt
                      ? ` - ${dayjs(details.eventDetails.endsAt).format("DD.MM.YYYY HH:mm")}`
                      : ""}
                  </Typography.Text>
                  <br />
                  {[details.eventDetails.venueName, details.eventDetails.venueAddress, details.eventDetails.city]
                    .filter(Boolean)
                    .join(", ")}
                </Descriptions.Item>
              )}

              {details.productDuration && (
                <Descriptions.Item label={t("productDetails.productDurationType")} span={3}>
                  <Typography.Text strong>
                    {t(`productDurationType.${details.productDuration.type}`)}
                    {details.productDuration.type === ProductDurationType.LastsFor
                      ? ` - ${t(`recurring.${details.productDuration.unit}`, {
                          count: details.productDuration.count,
                        })}`
                      : details.productDuration.type === ProductDurationType.UntilDate
                        ? `- ${dayjs(details.productDuration.date).format("DD.MM.YYYY")}`
                        : ""}
                  </Typography.Text>
                </Descriptions.Item>
              )}

              <Descriptions.Item label={t("productDetails.paymentType")} span={3}>
                {t(details.chargeType !== ProductChargeType.FREE ? `productDetails.paid` : "productDetails.isFree")}
              </Descriptions.Item>

              {details.chargeType !== ProductChargeType.FREE && (
                <Descriptions.Item label={t("productDetails.enabledPaymentGateways")} span={3}>
                  <ProductPaymentGatewaysStatus
                    productId={details.id}
                    enabledPaymentGateways={details.enabledPaymentGateways ?? undefined}
                  />
                </Descriptions.Item>
              )}

              {details.chargeType !== ProductChargeType.FREE && (
                <Descriptions.Item label={t("productDetails.invoiceClient")} span={3}>
                  {details.customInvoicesClient
                    ? `${details.customInvoicesClient.accountName} - ${t(
                        `common:invoices.${details.customInvoicesClient.client}`,
                      )}`
                    : "-"}
                </Descriptions.Item>
              )}

              {details.chargeType !== ProductChargeType.FREE && (
                <Descriptions.Item label="Konto Stripe" span={3}>
                  {details.customStripeAccount && stripeOptionsByKey[details.customStripeAccount]
                    ? `${stripeOptionsByKey[details.customStripeAccount]?.name} - ${stripeOptionsByKey[details.customStripeAccount]?.accountId}`
                    : "Główne"}
                </Descriptions.Item>
              )}

              {details.chargeType !== ProductChargeType.FREE && (
                <Descriptions.Item label="Konto Tpay (posId)" span={3}>
                  {details.tpayIntegration ? tpayOptionsByKey[details.tpayIntegration]?.posId || "Główne" : "Główne"}
                </Descriptions.Item>
              )}

              <Descriptions.Item label={t("productDetails.active")} span={3}>
                <Switch
                  loading={productDetailsStatus === RequestStatus.UPDATING}
                  checked={details.status === ProductStatus.ACTIVE}
                  onChange={() =>
                    dispatch(
                      productActions.updateProduct({
                        payload: {
                          id: details.id,
                          status:
                            details.status === ProductStatus.ACTIVE ? ProductStatus.INACTIVE : ProductStatus.ACTIVE,
                          description: details.description ?? "",
                          images: details.images,
                          name: details.name,
                        },
                      }),
                    )
                  }
                />
              </Descriptions.Item>

              <Descriptions.Item label={t("productDetails.linkToFree")} span={3}>
                <Tooltip title={t("list.copyToClipboard")}>
                  <LinkOutlined
                    style={{ fontSize: 22 }}
                    onClick={() =>
                      navigator.clipboard.writeText(
                        getProductLink({
                          productId: details.id,
                          domain: import.meta.env.VITE_ECOMMERCE_DOMAIN,
                          customDomain: productAutomation?.settings?.embeddedForm?.customDomain,
                          ...(details.chargeType === ProductChargeType.FREE ? { priceId: "free" } : {}),
                        }),
                      )
                    }
                  />
                </Tooltip>
              </Descriptions.Item>

              <Descriptions.Item label={t("productDetails.totalClients")} span={3}>
                {details.product_stats?.totalClients || 0}
              </Descriptions.Item>
              {details.chargeType !== ProductChargeType.FREE && (
                <>
                  <Descriptions.Item label={t("productDetails.activeClients")} span={3}>
                    <Tooltip title={t("productDetails.activeClientsDesc")}>
                      {details.product_stats?.activeClients ?? "-"}
                    </Tooltip>
                  </Descriptions.Item>
                  <Descriptions.Item label={t("productDetails.churnedClients")} span={3}>
                    <Tooltip title={t("productDetails.churnedClientsDesc")}>
                      {details.product_stats?.archivedClients ?? "-"}
                    </Tooltip>
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          )}
        </Space>
      </Card>
      <ModalForm<ProductFormModel>
        okText={t("common:button.update")}
        open={showProductForm}
        onCancel={() => {
          toggleProductForm(false);
          setProductModel(null);
        }}
        title={t<string>("products:form.editTitle")}
        loading={productDetailsStatus === RequestStatus.UPDATING}
      >
        <ProductForm onSubmit={handleSubmit} model={productModel} />
      </ModalForm>
    </>
  );
};

export default ProductDetails;
